import { TreeView } from "@/components/tree-view";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EmailFolder,
  EmailFolderEntity,
  EmailThreadEntity,
} from "@cohesive/api-entities";
import {
  EmailThreadCard,
  EmailThreadCardSkeleton,
} from "@cohesive/ui-components";
import { ErrorBoundary } from "@sentry/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDebounce } from "@uidotdev/usehooks";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import {
  buildLabelTree,
  DEFAULT_FOLDERS_ORDER,
  getMailFolderIcon,
  LabelTreeNode,
  MessageTabConfig,
  TreeDataItem,
} from "./types";
import { useGmailFoldersList } from "./useGmailFoldersList";
import { useGmailThreadsList } from "./useGmailThreadsList";

export const GmailSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const { data: folders, isLoading: isLabelsLoading } =
    useGmailFoldersList(connection);

  const allLabels = useMemo(() => {
    return folders?.data || ([] as EmailFolderEntity[]);
  }, [folders?.data]);

  // Process labels into system and user groups
  const { systemLabels, userLabels } = useMemo(() => {
    const system: EmailFolder[] = [];
    const user: EmailFolder[] = [];

    allLabels.forEach((label) => {
      // Skip labels that are hidden from the label list (except ALL_MAIL)
      if (label.data.hidden && label.data.name !== "ALL_MAIL") {
        return;
      }

      if (label.data.type === "system") {
        system.push(label.data);
      } else {
        user.push(label.data);
      }
    });

    // Sort system labels according to defined order
    system.sort((a, b) => {
      const indexA = DEFAULT_FOLDERS_ORDER.indexOf(a.name);
      const indexB = DEFAULT_FOLDERS_ORDER.indexOf(b.name);
      if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return { systemLabels: system, userLabels: user };
  }, [allLabels]);

  // Build tree data structure for user labels
  const userLabelTree = useMemo(() => {
    return buildLabelTree(userLabels);
  }, [userLabels]);

  // Generate tree view data for system labels
  const systemTreeItems = useMemo(() => {
    const items: TreeDataItem[] = systemLabels.map((label) => {
      const Icon = getMailFolderIcon(label.name);
      return {
        id: label.id,
        name: label.name
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase()),
        icon: Icon,
        labelType: "system",
        onClick: () => {
          setSelectedTab({
            type: "message",
            label: label.name,
            icon: <Icon className="h-4 w-4" />,
          });
        },
      };
    });
    return items;
  }, [systemLabels]);

  // Process user label tree into TreeDataItems
  const processLabelTreeToTreeData = useCallback(
    (node: LabelTreeNode): TreeDataItem => {
      const Icon = getMailFolderIcon(node.type);
      const hasChildren = node.children && node.children.length > 0;

      const treeItem: TreeDataItem = {
        id: node.id,
        name: node.name,
        icon: Icon,
        labelType: "user",
        onClick: () => {
          setSelectedTab({
            type: "message",
            label: node.fullPath,
            icon: <Icon className="h-4 w-4" />,
          });
        },
      };

      if (hasChildren) {
        treeItem.children = node.children.map((child) =>
          processLabelTreeToTreeData(child),
        );
      }

      return treeItem;
    },
    [],
  );

  // Generate complete tree view data
  const treeData = useMemo(() => {
    const userTreeItems = userLabelTree.map((node) =>
      processLabelTreeToTreeData(node),
    );

    // Combine system labels and user labels into a single flat array
    return [...systemTreeItems, ...userTreeItems];
  }, [systemTreeItems, userLabelTree, processLabelTreeToTreeData]);

  // Set initial selected tab
  const initialTab = useMemo<MessageTabConfig>(() => {
    const inboxLabel = systemLabels.find((label) => label.name === "INBOX");
    if (inboxLabel) {
      const Icon = getMailFolderIcon("INBOX");
      return {
        type: "message",
        label: "INBOX",
        icon: <Icon className="h-4 w-4" />,
      };
    }

    const AllMailIcon = getMailFolderIcon("ALL_MAIL");
    return {
      type: "message",
      label: "ALL_MAIL",
      icon: <AllMailIcon className="h-4 w-4" />,
    };
  }, [systemLabels]);

  const [selectedTab, setSelectedTab] = useState<MessageTabConfig>(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);
  const parentRef = useRef<HTMLDivElement>(null);

  // Update selected tab when labels load and INBOX becomes available
  useEffect(() => {
    if (systemLabels.length > 0 && selectedTab.label === "ALL_MAIL") {
      const inboxLabel = systemLabels.find((label) => label.name === "INBOX");
      if (inboxLabel) {
        const Icon = getMailFolderIcon("INBOX");
        setSelectedTab({
          type: "message",
          label: "INBOX",
          icon: <Icon className="h-4 w-4" />,
        });
      }
    }
  }, [systemLabels, selectedTab.label]);

  // API query with infinite scroll

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useGmailThreadsList(connection, {
    query: debouncedQuery,
    folder: selectedTab.label,
  });

  // Generic projections (entity_format=both) keyed by thread id — placed on the
  // canvas in place of the typed thread.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleRecordSelect = (
    thread: EmailThreadEntity,
    messageId?: string,
  ) => {
    // Place the generic projection (what lands on the canvas), falling back to
    // the typed thread when no generic is available.
    const generic = genericById.get(thread.data.id);
    const placeable = generic ?? thread;

    // If messageId is provided, embed it in the id to represent the specific
    // message. This lets the AI focus on the message while keeping thread context.
    if (messageId) {
      onSelect({
        ...placeable,
        data: {
          ...placeable.data,
          id: `${placeable.data.id}:message:${messageId}`,
        },
      } as typeof placeable);
    } else {
      onSelect(placeable);
    }
  };

  // Setup virtualization and transform thread data to match api-entities types
  const threads = useMemo(() => {
    const rawThreads =
      data?.pages.flatMap((page) => page.data).filter(Boolean) || [];

    // Transform threads to ensure proper typing for recipients and participants
    return rawThreads.map((thread: any) => ({
      ...thread,
      data: {
        ...thread.data,
        participants:
          thread.data.participants?.map((participant: any) => ({
            ...participant,
            type: participant.type || "to", // Default to "to" if not specified
          })) || [],
        messages:
          thread.data.messages?.map((message: any) => ({
            ...message,
            recipients:
              message.recipients?.map((recipient: any) => ({
                ...recipient,
                type: recipient.type || "to", // Default to "to" if not specified
              })) || [],
          })) || [],
      },
    }));
  }, [data?.pages]);

  const threadIds = useMemo(() => threads.map((t) => t.data.id), [threads]);

  const rowVirtualizer = useVirtualizer({
    count: threads.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      if (threads[index]?.data?.messages?.length > 0) {
        return 118;
      }
      return 86;
    },
    overscan: 5,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    onChange: (instance) => {
      const lastItem = instance.getVirtualItems().at(-1);
      if (
        lastItem &&
        lastItem.index >= threads.length - 5 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
  });

  return (
    <Selector>
      <SelectorSidebar>
        {isLabelsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView data={treeData} />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={`Search ${selectedTab.label
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase())}...`}
        autoFocus
      />
      <SelectorResults ref={parentRef}>
        {isLoading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <EmailThreadCardSkeleton
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                key={i}
              />
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            {error ? <p>Error loading threads</p> : <p>No threads found</p>}
          </div>
        ) : (
          <ErrorBoundary
            fallback={
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <p>Error loading threads</p>
              </div>
            }
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const thread = threads[virtualRow.index];

                return (
                  <div
                    key={thread.data.id}
                    data-index={virtualRow.index}
                    ref={(node) => rowVirtualizer.measureElement(node)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <EntityDragRow
                      entity={thread as EmailThreadEntity}
                      generic={genericById.get(thread.data.id)}
                      entityId={thread.data.id}
                      orderedIds={threadIds}
                      onSelect={() =>
                        handleRecordSelect(thread as EmailThreadEntity)
                      }
                      dragScope="gmail"
                    >
                      <EmailThreadCard
                        onMentionClick={(messageId) =>
                          handleRecordSelect(
                            thread as EmailThreadEntity,
                            messageId,
                          )
                        }
                        platform={thread.platform}
                        {...thread.data}
                        className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                      />
                    </EntityDragRow>
                  </div>
                );
              })}
            </div>
            {isFetchingNextPage && (
              <div>
                {[1, 2, 3].map((i) => (
                  <EmailThreadCardSkeleton
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                    key={i}
                  />
                ))}
              </div>
            )}
          </ErrorBoundary>
        )}
      </SelectorResults>
    </Selector>
  );
};
